import { useState } from "react";
import Form from "react-bootstrap/Form";
import { useNavigate } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstLiveTable from "../../components/DstTable/DstLiveTable";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import { useSession } from "../../contexts/SessionContext";
import { EvaluationTypeEnum } from "../../models/EvaluationTypes";
import { PermissionComponent } from "../../services/PermissionGuard";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { generateRemoteUrl } from "./Evaluations.function";
import EvaluationCreationModal from "./components/CreationModal";

export const Evaluations = () => {
  const { language, permissions } = useSession();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeprecated, setShowDeprecated] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [showModalCreation, setShowModalCreation] = useState(false);

  const columns = [
    {
      name: translate(language, "GLOBAL.ID"),
      key: "id",
      sortable: true,
    },
    {
      name: translate(language, "GLOBAL.NAME"),
      key: "name",
      sortable: true,
    },
    {
      name: translate(language, "GLOBAL.DESCRIPTION"),
      key: "description",
      sortable: true,
    },
    {
      name: translate(language, "PAGES.EVALUATIONS.S3_NAME"),
      key: "s3_name",
      sortable: true,
    },
    {
      name: translate(language, "PAGES.EVALUATIONS.CREATED_AT"),
      key: "dt_created",
      sortable: true,
      render: (item: any) => <span>{item.dt_created ? formatDateHumanly(language, item.dt_created) : "-"}</span>,
    },
    {
      name: translate(language, "PAGES.EVALUATIONS.UPDATED_AT"),
      key: "dt_updated",
      sortable: true,
      render: (item: any) => <span>{item.dt_updated ? formatDateHumanly(language, item.dt_updated) : "-"}</span>,
    },
    {
      name: translate(language, "GLOBAL.TYPE"),
      key: "type",
      sortable: true,
    },
    {
      name: translate(language, "PAGES.EVALUATIONS.AVAILABLE"),
      key: "is_deprecated",
      render: (item: any) => (
        <div className="text-center">
          {!item.is_deprecated ? (
            <span className="dst-icon-check green-font" />
          ) : (
            <span className="dst-icon-x dark-red-font" />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="px-3 pt-3">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div className="d-flex w-fit align-items-center justify-content-between">
          <h1 className="mb-0 me-3">{translate(language, "PAGES.EVALUATIONS.TITLE")}</h1>
          <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
        </div>
        <div className="d-flex justify-content-end">
          <div className="d-flex align-items-center gap-3">
            <MultiFilterDropdown
              tags={Object.values(EvaluationTypeEnum).map((type) => ({
                display: type,
                value: type,
              }))}
              onSelectTags={(elem) => setSelectedTypes(elem as string[])}
              showSearchBar
              inputTitle={translate(language, "GLOBAL.TYPE")}
              language={language}
            />
            <DstButton
              btnClass="h-100 d-flex align-items-center"
              btnImageBefore={
                <Form.Check
                  type="switch"
                  className="check-box s-bold me-2"
                  id="show-deprecated-switch"
                  checked={showDeprecated}
                  onChange={() => setShowDeprecated(!showDeprecated)}
                />
              }
              value={translate(language, "PAGES.EVALUATIONS.SHOW_DEPRECATED")}
              variant="light"
              btnHeight="50px"
              btnWidth="200px"
              clickFunction={() => setShowDeprecated(!showDeprecated)}
            />
            <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/evaluations" }]}>
              <DstButton
                value={translate(language, "PAGES.EVALUATIONS.BUTTON_ADD_EVALUATION")}
                btnImageBefore={<span className="me-2 dst-icon-plus" />}
                clickFunction={() => setShowModalCreation(true)}
              />
            </PermissionComponent>
          </div>
        </div>
      </div>

      <DstLiveTable
        classTable="text-nowrap"
        remote={generateRemoteUrl(searchQuery, showDeprecated, selectedTypes)}
        remoteKey="evaluations"
        columns={columns}
        pagination
        paginationAlwaysShowFooter
        paginationResizeOptions={[25, 50, 100]}
        itemsPerPage={50}
        striped
        onRowClick={(event, item) => completeNavigate(event, navigate, `/evaluation/${item.id}`)}
      />
      <EvaluationCreationModal
        language={language}
        setShowCreationModal={setShowModalCreation}
        showCreationModal={showModalCreation}
        permissions={permissions}
      />
    </div>
  );
};

export default Evaluations;
